'use client';

import type { SupabaseClient } from '@supabase/supabase-js';
import { createBrowserClient } from '@supabase/ssr';

import { AUTH_SESSION_MAX_AGE_SECONDS } from '@packages/config';
import { getSupabaseBrowserClient } from '../../../packages/supabase';

type GenericClient = SupabaseClient;

const clientCache = new Map<string, GenericClient>();

const buildFromEnv = () => {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const key = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY;
  if (!url || !key) {
    if (typeof window !== 'undefined') {
      console.warn('[supabase] fallback env missing', {
        hasUrl: Boolean(url),
        hasAnon: Boolean(process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY),
        hasPublishable: Boolean(process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY),
      });
    }
    return null;
  }
  return createBrowserClient(url, key, {
    auth: {
      flowType: 'pkce',
      detectSessionInUrl: true,
      autoRefreshToken: true,
      persistSession: true,
    },
    cookieOptions: {
      path: '/',
      sameSite: 'lax',
      maxAge: AUTH_SESSION_MAX_AGE_SECONDS,
    },
  }) as GenericClient;
};

export const getSupabaseBrowserClientSafe = (
  app?: Parameters<typeof getSupabaseBrowserClient>[0],
): GenericClient | null => {
  const key = app ? `browser-${app}` : 'browser-default';
  const existing = clientCache.get(key);
  if (existing) {
    return existing;
  }
  try {
    const client = getSupabaseBrowserClient(app) as GenericClient;
    clientCache.set(key, client);
    return client;
  } catch (err) {
    if (typeof window !== 'undefined') {
      const debug = {
        hasUrl: Boolean(process.env.NEXT_PUBLIC_SUPABASE_URL),
        hasAnon: Boolean(process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY),
        hasPublishable: Boolean(process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY),
        error: err instanceof Error ? err.message : String(err),
      };
      console.warn('[supabase] client init failed', debug);
    }
    const fallback = buildFromEnv();
    if (fallback) {
      clientCache.set(key, fallback);
      return fallback;
    }
    return null;
  }
};

export const supabaseBrowserClient = getSupabaseBrowserClientSafe();
